import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from '../../features/workspace/WorkspaceContext';
import WorkspaceLayout from '../../features/workspace/components/WorkspaceLayout';
import { DEFAULT_WORKSPACE_PATH, ONBOARDING_PATH } from '../../features/workspace/navigation';
import FrontDesk from '../../features/workspace/pages/front-desk/FrontDesk';
import Calendar from '../../features/workspace/pages/Calendar';
import MessagesLayout from '../../features/workspace/pages/messages/MessagesLayout';
import MessagesWhatsApp from '../../features/workspace/pages/messages/MessagesWhatsApp';
import MessagesTemplates from '../../features/workspace/pages/messages/MessagesTemplates';
import MessagesReminders from '../../features/workspace/pages/messages/MessagesReminders';
import SalesLayout from '../../features/workspace/pages/sales/SalesLayout';
import SalesTransactions from '../../features/workspace/pages/sales/SalesTransactions';
import SalesCashOutConfirm from '../../features/workspace/pages/sales/SalesCashOutConfirm';
import SalesDeposits from '../../features/workspace/pages/sales/SalesDeposits';
import Clients from '../../features/workspace/pages/Clients';
import ReportsLayout from '../../features/workspace/pages/reports/ReportsLayout';
import ReportsOverview from '../../features/workspace/pages/reports/ReportsOverview';
import ReportsSalesSummary from '../../features/workspace/pages/reports/ReportsSalesSummary';
import ReportsStaff from '../../features/workspace/pages/reports/ReportsStaff';
import ReportsRetention from '../../features/workspace/pages/reports/ReportsRetention';
import ReportsReconciliation from '../../features/workspace/pages/reports/ReportsReconciliation';
import Marketing from '../../features/workspace/pages/Marketing';
import IntegrationsLayout from '../../features/workspace/pages/integrations/IntegrationsLayout';
import IntegrationsOverview from '../../features/workspace/pages/integrations/IntegrationsOverview';
import ManageLayout from '../../features/workspace/pages/manage/ManageLayout';
import ManageServices from '../../features/workspace/pages/manage/ManageServices';
import ManageHours from '../../features/workspace/pages/manage/ManageHours';
import ManageStaff from '../../features/workspace/pages/manage/ManageStaff';
import ManageStaffSchedule from '../../features/workspace/pages/manage/ManageStaffSchedule';
import ManageBookingLink from '../../features/workspace/pages/manage/ManageBookingLink';
import ManagePayments from '../../features/workspace/pages/manage/ManagePayments';
import ManageLocations from '../../features/workspace/pages/manage/ManageLocations';
import ManageProfile from '../../features/workspace/pages/manage/ManageProfile';
import Onboarding from '../../features/workspace/pages/Onboarding';

const WorkspaceLoading = () => (
  <div className="flex min-h-screen items-center justify-center bg-sand-100">
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
  </div>
);

const RequireOnboardingComplete = ({ children }) => {
  const { business, loading } = useWorkspace();
  const location = useLocation();

  if (loading) {
    return <WorkspaceLoading />;
  }

  if (business && !business.onboardingComplete) {
    return <Navigate to={ONBOARDING_PATH} replace state={{ from: location.pathname }} />;
  }

  return children;
};

export const WorkspaceRoutes = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <WorkspaceLoading />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ returnTo: location.pathname }} replace />;
  }

  return (
    <WorkspaceProvider>
      <Routes>
        <Route path="onboarding" element={<Onboarding />} />
        <Route
          element={
            <RequireOnboardingComplete>
              <WorkspaceLayout />
            </RequireOnboardingComplete>
          }
        >
          <Route index element={<Navigate to={DEFAULT_WORKSPACE_PATH} replace />} />
          <Route path="front-desk">
            <Route index element={<Navigate to="today" replace />} />
            <Route path=":section" element={<FrontDesk />} />
          </Route>
          <Route path="calendar" element={<Calendar />} />
          <Route path="messages" element={<MessagesLayout />}>
            <Route index element={<Navigate to="whatsapp" replace />} />
            <Route path="whatsapp" element={<MessagesWhatsApp />} />
            <Route path="templates" element={<MessagesTemplates />} />
            <Route path="reminders" element={<MessagesReminders />} />
            <Route path="inbox" element={<Navigate to="../whatsapp" replace />} />
            <Route path="sms-email" element={<Navigate to="../whatsapp" replace />} />
          </Route>
          <Route path="sales" element={<SalesLayout />}>
            <Route index element={<Navigate to="transactions" replace />} />
            <Route path="transactions" element={<SalesTransactions />} />
            <Route path="transactions/cash-out" element={<SalesCashOutConfirm />} />
            <Route path="deposits" element={<SalesDeposits />} />
            <Route path="invoices" element={<Navigate to="transactions" replace />} />
            <Route path="vouchers" element={<Navigate to="transactions" replace />} />
            <Route path="tips" element={<Navigate to="transactions" replace />} />
          </Route>
          <Route path="clients" element={<Clients />} />
          <Route
            path="business-agent"
            element={<Navigate to="/app/front-desk/today?agent=1" replace />}
          />
          <Route
            path="business-agent/*"
            element={<Navigate to="/app/front-desk/today?agent=1" replace />}
          />
          <Route path="sera" element={<Navigate to="/app/front-desk/today?agent=1" replace />} />
          <Route path="sera/*" element={<Navigate to="/app/front-desk/today?agent=1" replace />} />
          <Route path="reports" element={<ReportsLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<ReportsOverview />} />
            <Route path="sales-summary" element={<ReportsSalesSummary />} />
            <Route path="staff" element={<ReportsStaff />} />
            <Route path="retention" element={<ReportsRetention />} />
            <Route path="reconciliation" element={<ReportsReconciliation />} />
          </Route>
          <Route path="marketing" element={<Marketing />} />
          <Route path="integrations" element={<IntegrationsLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<IntegrationsOverview />} />
            <Route path="payments" element={<Navigate to="../overview" replace />} />
            <Route path="mobile-money" element={<Navigate to="../overview" replace />} />
            <Route path="messaging" element={<Navigate to="../overview" replace />} />
            <Route path="discovery" element={<Navigate to="../overview" replace />} />
          </Route>
          <Route path="manage" element={<ManageLayout />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ManageProfile />} />
            <Route path="services" element={<ManageServices />} />
            <Route path="hours" element={<ManageHours />} />
            <Route path="staff" element={<ManageStaff />} />
            <Route path="staff-schedule" element={<ManageStaffSchedule />} />
            <Route path="booking-link" element={<ManageBookingLink />} />
            <Route path="payments" element={<ManagePayments />} />
            <Route path="locations" element={<ManageLocations />} />
          </Route>
          <Route path="*" element={<Navigate to={DEFAULT_WORKSPACE_PATH} replace />} />
        </Route>
      </Routes>
    </WorkspaceProvider>
  );
};
